import { useState, type FormEvent } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { apiRequest } from "../lib/api"
import type { ApiError } from "../lib/api"
import type { Project } from "../types"

type CreateProjectResponse = {
  project: Project
}

const Onboarding = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [name, setName] = useState("")

  const createMutation = useMutation<CreateProjectResponse, ApiError>({
    mutationFn: () =>
      apiRequest<CreateProjectResponse>("/api/projects", {
        method: "POST",
        body: JSON.stringify({ project: { name } }),
      }),
    onSuccess: (payload) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] })
      queryClient.invalidateQueries({ queryKey: ["me"] })
      navigate(`/${payload.project.slug}`)
    },
  })

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!name.trim()) return
    createMutation.mutate()
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="w-full max-w-lg rounded-lg border bg-card p-8 text-card-foreground shadow-xs">
        <h1 className="text-2xl font-semibold tracking-tight">
          Create your first project
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Accounts start empty. Create a project to continue.
        </p>
        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <Input
            placeholder="Project name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
          />
          {createMutation.isError ? (
            <p className="text-sm text-destructive">
              {createMutation.error.message}
            </p>
          ) : null}
          <Button
            type="submit"
            disabled={createMutation.isPending || !name.trim()}
            className="w-full"
          >
            {createMutation.isPending ? "Creating..." : "Create project"}
          </Button>
        </form>
      </div>
    </div>
  )
}

export default Onboarding
